import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAssetDetail } from '../api/hooks';
import { useMultiMarketProbability } from '../api/useMultiMarketProbability';
import { SectionCard } from '../components/shared';
import { PageFrame } from '../../../components/common/page/PageFrame';
import { assignMarketColors } from '../utils/marketColors';
import { ChartStack } from '../event/chart/ChartStack';
import { seriesChange } from '../event/chart/changeOverRange';
import {
    fullscreenCardSx,
    fullscreenChartHeight,
} from '../event/chart/fullscreen';
import type { ChartSeries, SeriesMoves } from '../event/chart/seriesTypes';
import { useCompactChart } from '../event/chart/useCompactChart';
import { useFullscreenFill } from '../event/chart/useFullscreenFill';
import { AssetToolbar } from './chart/AssetToolbar';
import { eventMoves, eventSeries, onMainFromMoves } from './chart/eventSeries';
import {
    PRICE_SERIES_ID,
    priceSeries,
    type PriceMode,
} from './chart/priceSeries';
import { usePriceData } from './chart/usePriceData';
import { hasVolume, volumeSeries } from './chart/volumeSeries';
import { CandidateEventList } from './events/CandidateEventList';
import { leadingOutcome } from './events/leadingOutcome';
import { useCandidateEvents } from './events/useCandidateEvents';
import { AssetHeader } from './header/AssetHeader';
import { priceFigures } from './header/priceFigures';
import { assetFromDetail, eventKey } from './model/fromAssetDetail';
import type { CandidateEvent } from './model/types';
import { MAX_SELECTED_EVENTS, useAssetSelection } from './useAssetSelection';
import { useAssetWindow } from './useAssetWindow';
import { activeQuickRange, assetRangeLabel, rangeFor } from './window';

const CHART_HEIGHT_SHARE = 0.55;
// Below 900 px the strip and the toolbar sit above the chart (the mockup: a
// 340 px plot and its axis on an 844 px screen).
const CHART_HEIGHT_SHARE_COMPACT = 0.45;
const MIN_CHART_HEIGHT = 320;
const PANEL_TITLES = { probability: 'Price' };
const NO_MAIN: ReadonlySet<string> = new Set();

function useWindowHeight(): number {
    const [height, setHeight] = useState(() => window.innerHeight);
    useEffect(() => {
        const onResize = () => setHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return height;
}

export function AssetView() {
    const { assetType = null, ticker = null } = useParams<{
        assetType: string;
        ticker: string;
    }>();
    const theme = useTheme();
    const compact = useCompactChart();
    const fill = useFullscreenFill();

    const detail = useAssetDetail(assetType, ticker);
    const asset = useMemo(
        () =>
            assetType && ticker && (detail.data || !detail.loading)
                ? assetFromDetail(detail.data, assetType, ticker)
                : null,
        [detail.data, detail.loading, assetType, ticker]
    );

    const win = useAssetWindow();
    const price = usePriceData(ticker, assetType, win.range, win.resolution);

    const selection = useAssetSelection();
    const [showClosed, setShowClosed] = useState(false);
    const [closedWanted, setClosedWanted] = useState(false);
    useEffect(() => {
        setShowClosed(false);
        setClosedWanted(false);
    }, [assetType, ticker]);
    const candidates = useCandidateEvents(ticker, assetType, closedWanted);
    const missingSelected =
        candidates.loaded &&
        selection.selectedKeys.some(
            key => !candidates.events.some(e => eventKey(e) === key)
        );
    useEffect(() => {
        if (showClosed || missingSelected) setClosedWanted(true);
    }, [showClosed, missingSelected]);
    const selectedEvents = useMemo(
        () =>
            selection.selectedKeys
                .map(key => candidates.events.find(e => eventKey(e) === key))
                .filter((e): e is CandidateEvent => e !== undefined),
        [selection.selectedKeys, candidates.events]
    );
    const colors = useMemo(
        () => assignMarketColors(selection.selectedKeys),
        [selection.selectedKeys]
    );
    const marketIds = useMemo(
        () =>
            selectedEvents
                .map(e => leadingOutcome(e)?.market.marketId)
                .filter((id): id is string => typeof id === 'string'),
        [selectedEvents]
    );
    const probability = useMultiMarketProbability(
        marketIds,
        win.range[0],
        win.range[1],
        win.resolution
    );

    const [mode, setMode] = useState<PriceMode>('candles');
    const [showVolume, setShowVolume] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [onMain, setOnMain] = useState<ReadonlySet<string>>(NO_MAIN);

    useEffect(() => {
        if (!fullscreen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fullscreen]);

    const windowHeight = useWindowHeight();
    const fit = fullscreenChartHeight(windowHeight, fill);
    const availableHeight = fullscreen
        ? fit.height
        : Math.max(
              MIN_CHART_HEIGHT,
              Math.round(
                  windowHeight *
                      (compact
                          ? CHART_HEIGHT_SHARE_COMPACT
                          : CHART_HEIGHT_SHARE)
              )
          );

    const volumeAvailable = hasVolume(price.points);
    const series = useMemo<ChartSeries[]>(() => {
        const list: ChartSeries[] = [
            priceSeries(
                price.points,
                ticker ?? '',
                theme.palette.text.primary,
                mode
            ),
        ];
        if (showVolume && volumeAvailable) {
            list.push(volumeSeries(price.points, theme.palette.text.primary));
        }
        list.push(...eventSeries(selectedEvents, probability.series, colors));
        return list;
    }, [
        price.points,
        ticker,
        mode,
        showVolume,
        volumeAvailable,
        selectedEvents,
        probability.series,
        colors,
        theme.palette.text.primary,
    ]);

    const moves = useMemo(() => eventMoves(series, onMain), [series, onMain]);
    const onMovesChange = (next: SeriesMoves) =>
        setOnMain(onMainFromMoves(series, next));

    const figures = useMemo(
        () => priceFigures(price.points, win.range),
        [price.points, win.range]
    );
    const priceChange = useMemo(() => {
        const s = series.find(x => x.id === PRICE_SERIES_ID);
        return s
            ? seriesChange(
                  s,
                  win.range[0].getTime(),
                  win.range[1].getTime(),
                  // the number the header shows, so the change adds up to it
                  figures.last ?? undefined
              )
            : null;
    }, [series, win.range, figures.last]);
    const activeRange = activeQuickRange(win.range, win.bounds);
    const rangeLabel = assetRangeLabel(win.range, win.bounds);
    const onQuickRange = (id: string) => {
        const next = rangeFor(id, win.bounds);
        if (next) win.setRange(next);
    };

    if (!assetType || !ticker) return null;

    return (
        <PageFrame>
            {detail.error && !detail.data && (
                <Alert severity="warning">
                    The details of this asset could not be loaded. The price
                    chart can still work.
                </Alert>
            )}

            <AssetHeader
                asset={asset}
                loading={detail.loading}
                figures={figures}
                candidateCount={candidates.events.length}
                change={priceChange}
                rangeLabel={rangeLabel}
            />

            <SectionCard
                sx={
                    fullscreen
                        ? fullscreenCardSx(theme.zIndex.modal, fill)
                        : { p: 1.5 }
                }
            >
                <AssetToolbar
                    assetType={assetType}
                    displayRange={win.range}
                    activeRange={activeRange}
                    onQuickRange={onQuickRange}
                    mode={mode}
                    onModeChange={setMode}
                    resolutionChoice={win.resolutionChoice}
                    resolutionLabel={win.resolutionLabel}
                    onResolutionChange={win.setResolutionChoice}
                    volumeAvailable={volumeAvailable}
                    showVolume={showVolume}
                    onToggleVolume={() => setShowVolume(v => !v)}
                    fullscreen={fullscreen}
                    onToggleFullscreen={() => setFullscreen(v => !v)}
                />
                {price.error && price.points.length === 0 ? (
                    <Typography
                        sx={{
                            fontSize: '12px',
                            color: 'text.secondary',
                            py: 4,
                            textAlign: 'center',
                        }}
                    >
                        No price data for this range.
                    </Typography>
                ) : (
                    <ChartStack
                        series={series}
                        moves={moves}
                        onMovesChange={onMovesChange}
                        bounds={win.bounds}
                        range={win.range}
                        onRangeChange={win.setRange}
                        availableHeight={availableHeight}
                        minMainHeight={fullscreen ? fit.floor : undefined}
                        fill={fullscreen && fill}
                        loading={price.loading && price.points.length === 0}
                        panelTitles={PANEL_TITLES}
                        rangeLabel={rangeLabel}
                    />
                )}
            </SectionCard>

            <SectionCard sx={{ p: 1.5 }}>
                <CandidateEventList
                    events={candidates.events}
                    loading={candidates.loading}
                    error={candidates.error}
                    selectedKeys={selection.selectedKeys}
                    maxSelected={MAX_SELECTED_EVENTS}
                    colors={colors}
                    onToggle={selection.toggleEvent}
                    onClear={selection.clearEvents}
                    showClosed={showClosed}
                    onShowClosedChange={setShowClosed}
                    closedLoaded={candidates.closedLoaded}
                    closedLoading={candidates.closedLoading}
                />
            </SectionCard>
        </PageFrame>
    );
}
